import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import '../styles/Auth.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = Array.isArray(data.detail) ? data.detail.map(d => d.msg).join(', ') : (data.detail || 'Could not send reset link.');
        throw new Error(msg);
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background">
        <video className="auth-video" autoPlay loop muted playsInline>
          <source src="/hero_video.mp4" type="video/mp4" />
        </video>
        <div className="auth-video-overlay"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: 480 }}>
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
                alt="Ezai Build"
                className="logo-icon"
                style={{ width: 44, height: 44 }}
              />
              <span className="logo-text">Ezai Build</span>
            </Link>
            <h1 className="display-medium auth-title">Forgot password?</h1>
            <p className="body-medium auth-subtitle">
              {sent
                ? "Check your inbox for a reset link."
                : "Enter the email tied to your account and we'll send you a reset link."}
            </p>
          </div>

          {sent ? (
            <div data-testid="forgot-sent-state" style={{ textAlign: 'center', padding: '16px 0' }}>
              <CheckCircle2 size={48} color="var(--brand-primary)" style={{ marginBottom: 16 }} />
              <p className="body-medium" style={{ marginBottom: 8 }}>
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
              </p>
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                The link expires in 1 hour. Didn't receive it? Check your spam folder or try again in a few minutes.
              </p>
              <Link to="/login" className="auth-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 24 }}>
                <ArrowLeft size={16} /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label className="form-label body-medium">Email</label>
                <div className="input-wrapper">
                  <Mail size={20} className="input-icon" />
                  <input
                    type="email"
                    className="form-input"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              {error && <p className="auth-error body-small" data-testid="auth-error">{error}</p>}

              <Button
                type="submit"
                className="btn-primary btn-large auth-btn"
                disabled={submitting}
                data-testid="forgot-submit-btn"
              >
                {submitting ? <Loader2 size={20} className="spinner" /> : 'Send reset link'}
              </Button>

              <p className="auth-switch body-medium" style={{ marginTop: 24 }}>
                Remembered it?{' '}
                <Link to="/login" className="auth-link">Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
